import React from "react";
import classNames from "classnames";
import { NavLink } from "react-router";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import Tooltip from "features/components/Tooltip";

import { SIDEBAR_ICON_SIZE } from "./constants";
import IconRenderer from "./IconRenderer";
import LiItem from "./LiItem";
import { NavItem } from "./types";

interface Props {
  item: NavItem;
  menuExpanded: boolean;
  onClick?: () => void;
}

const getNavBarItemClasses = (menuExpanded: boolean, isActive?: boolean) =>
  classNames(
    "flex items-center py-2 space-x-6 rounded-sm hover:bg-surface-subtle text-base h-[40px]",
    {
      "justify-center mx-2 w-[2.5rem] h-[2.5rem]": !menuExpanded,
      "bg-surface-selected hover:bg-surface-selected": isActive,
    }
  );

const NavBarItem = ({ item, menuExpanded, onClick }: Props) => {
  const { to, hasUnlistedSubroutes, icon, testId, ...otherLinkProps } = item;

  const navBarItem = (
    <NavLink
      to={to}
      end={!hasUnlistedSubroutes}
      data-testid={testId}
      {...otherLinkProps}
    >
      {({ isActive }) => (
        <div className={getNavBarItemClasses(menuExpanded, isActive)}>
          <div
            className={classNames({
              "pl-4": menuExpanded,
              "justify-center": !menuExpanded,
            })}
          >
            <IconRenderer icon={icon} size={SIDEBAR_ICON_SIZE} />
          </div>
          {menuExpanded && <div className="text-sm">{item.text}</div>}
        </div>
      )}
    </NavLink>
  );

  return (
    <LiItem onClick={onClick} menuExpanded={menuExpanded}>
      {!menuExpanded && (
        <Tooltip title={item.text} placement="right">
          {navBarItem}
        </Tooltip>
      )}
      {menuExpanded && navBarItem}
    </LiItem>
  );
};

interface ExternalNavBarItemProps {
  link: NavItem;
  menuExpanded: boolean;
}

export const ExternalNavBarItem = ({
  link,
  menuExpanded,
}: ExternalNavBarItemProps) => {
  const navBarItem = (
    <a
      href={link.to.toString()}
      target="_blank"
      rel="noreferrer"
      className={getNavBarItemClasses(menuExpanded)}
      data-testid={link.testId}
    >
      <div
        className={classNames({
          "pl-4": menuExpanded,
          "justify-center": !menuExpanded,
        })}
      >
        <IconRenderer icon={link.icon} size={SIDEBAR_ICON_SIZE} />
      </div>
      {menuExpanded && (
        <div className="flex items-center gap-2 text-sm">
          {link.text} <OpenInNewIcon className="inline ml-1" fontSize="small" />
        </div>
      )}
    </a>
  );

  return (
    <LiItem menuExpanded={menuExpanded}>
      {!menuExpanded && (
        <Tooltip title={link.text} placement="right">
          {navBarItem}
        </Tooltip>
      )}
      {menuExpanded && navBarItem}
    </LiItem>
  );
};

export default NavBarItem;
